import { CostFeeType, CostScheduleGroup, CostScheduleGroupKey } from '@bariendo/lib/payables';
import { RateGroup, RateVersion } from '../rates/rateSchedule';

/** The Cost schedule view's vocabulary (BAR-3047), as the spec names it. */

export const COST_FEE_TYPES: { value: CostFeeType; label: string; rateLabel: string }[] = [
  { value: 'FACILITY', label: 'Facility', rateLabel: 'Base fee' },
  { value: 'ANESTHESIA', label: 'Anesthesia', rateLabel: 'Base fee' },
  { value: 'DOCTOR', label: 'Doctor fee', rateLabel: 'Fee' },
  { value: 'PN_COMMISSION', label: 'PN commission', rateLabel: 'Commission per win' },
];

export const DAILY_MINIMUM = 'DAILY_MINIMUM';

export const COST_PROCEDURES: { value: string; label: string }[] = [
  { value: 'ESG', label: 'ESG' },
  { value: 'ESG_MAX', label: 'ESG Max' },
  { value: 'SIS', label: 'SIS' },
  { value: 'TORE', label: 'TORe' },
  { value: 'TORE_PLUS', label: 'TORe Plus' },
  { value: 'IGB_PLACEMENT', label: 'IGB placement' },
  { value: 'IGB_REMOVAL', label: 'IGB removal' },
];

export const PARTNER_PROCEDURES = [{ value: DAILY_MINIMUM, label: 'Daily minimum' }, ...COST_PROCEDURES];

/** `TORE_PLUS` → `TORe Plus`; an unknown code as-is. */
export function costProcedureLabel(procedure: string): string {
  return PARTNER_PROCEDURES.find((option) => option.value === procedure)?.label ?? procedure;
}

/** Facility and anesthesia: per partner and market, with variable fees and a daily minimum. */
export function isPartnerFee(feeType: CostFeeType): boolean {
  return feeType === 'FACILITY' || feeType === 'ANESTHESIA';
}

export function costGroupKey(group: CostScheduleGroupKey): string {
  return `${group.feeType}|${group.payeeRef}|${group.location}`;
}

/** `Tampa Bay Surgery Center · Greater Tampa`, `Every doctor`, `Every PN`. */
export function costGroupName(group: CostScheduleGroup): string {
  if (group.feeType === 'DOCTOR') return 'Every doctor';
  if (group.feeType === 'PN_COMMISSION') return 'Every PN';
  return `${group.payeeName ?? group.payeeRef} · ${group.location}`;
}

/** A cost schedule group on the shared rate-history table. */
export function toRateGroup(group: CostScheduleGroup): RateGroup {
  const partner = isPartnerFee(group.feeType);
  return {
    key: costGroupKey(group),
    name: costGroupName(group),
    versions: group.rates.map(
      (rate): RateVersion => ({
        id: rate.id,
        procedure: rate.procedure,
        rate: rate.baseFee,
        ...(partner && rate.procedure !== DAILY_MINIMUM
          ? {
              variable: {
                baseCoversMinutes: rate.baseCoversMinutes,
                increment: rate.increment,
                incrementEveryMinutes: rate.incrementEveryMinutes,
              },
            }
          : {}),
        effectiveFrom: rate.effectiveFrom,
        effectiveUntil: rate.effectiveUntil,
        setBy: rate.setBy,
        correctedAt: rate.correctedAt,
        correctedBy: rate.correctedBy,
        correctionReason: rate.correctionReason,
        previousRate: rate.previousValues?.base_fee ?? null,
      })
    ),
  };
}
